import React from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  DialogDescription,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { InCardContext } from '~/components/ui/card/Card';
import { ModalCloseButton } from '~/components/ui/modal/ModalCloseButton';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  size?: 'small' | 'default' | 'large' | 'xlarge' | 'full';
  /** Header title. Without it the caller supplies `titleId` for its own heading. */
  title?: string;
  description?: string;
  /** Right-aligned action row under the body. */
  footer?: React.ReactNode;
  /** While true, Escape, the backdrop and the close button are ignored. */
  busy?: boolean;
  closeOnBackdrop?: boolean;
  titleId?: string; // id of the element that labels the dialog (aria-labelledby)
  testId?: string; // rendered as data-testid on the dialog container
}

const sizeClasses = {
  small: 'max-w-md',
  default: 'max-w-2xl',
  large: 'max-w-4xl',
  xlarge: 'max-w-6xl',
  full: 'h-full max-w-none',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  showCloseButton = true,
  size = 'default',
  title,
  description,
  footer,
  busy = false,
  closeOnBackdrop = true,
  titleId,
  testId,
}) => {
  const full = size === 'full';
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      // Headless owns aria-labelledby for `title`; a caller's own heading is wired here, since the prop is not passed through.
      ref={(el: HTMLElement | null) => {
        if (el && !title && titleId) el.setAttribute('aria-labelledby', titleId);
      }}
      className="fixed inset-0 z-50"
      data-testid={testId}
      aria-label={title || titleId ? undefined : 'Dialog'}
    >
      <DialogBackdrop className="fixed inset-0 bg-black/40 dark:bg-white/10" />
      {/* The panel is the full-viewport scroller, so Headless never sees a click as "outside":
          the backdrop guard (busy, closeOnBackdrop) is ours, Escape is Headless's. */}
      <DialogPanel
        data-slot="modal-scrim"
        className={twMerge(
          'fixed inset-0 flex items-start justify-center overflow-y-auto',
          full ? '' : 'py-8',
        )}
        onClick={(event: React.MouseEvent) => {
          if (event.target === event.currentTarget && closeOnBackdrop) close();
        }}
      >
        {/* The panel portals to `document.body`, outside the protected layout that sets
            `dark:text-white/90`. Establishing the same default here keeps un-classed text
            (e.g. a bare `font-mono` span) from falling back to black on the dark panel. */}
        <div
          className={twMerge(
            'relative w-full bg-white text-gray-800 dark:bg-gray-900 dark:text-white/90',
            full ? '' : 'rounded-2xl',
            sizeClasses[size],
            className,
          )}
        >
          {showCloseButton && (
            <ModalCloseButton
              onClick={close}
              disabled={busy}
              className="absolute right-3 top-3 z-50 sm:right-6"
            />
          )}
          {title && (
            <div className="border-b border-gray-200 px-6 py-5 pr-16 dark:border-gray-800 sm:pr-20">
              <DialogTitle
                as="h2"
                id={titleId}
                className="text-xl font-bold text-gray-800 dark:text-white/90"
              >
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {description}
                </DialogDescription>
              )}
            </div>
          )}
          {/* Context crosses the portal: a modal opened from inside a Card is not itself in one, so its tables keep their frame. */}
          <InCardContext.Provider value={false}>
            <div className={title ? 'px-6 py-5' : undefined}>{children}</div>
          </InCardContext.Provider>
          {footer && (
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
              {footer}
            </div>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
};
